/**
 * StatusPill — semantic status indicator (job / candidate / interview / invitation).
 *
 * Soft `--color-*-soft` backgrounds; optional leading dot/icon.
 * Color prop is `variant` (status semantics), intentionally distinct from Tag's `intent`.
 *
 * Size scale (same as Tag, pairs with Button icon heights):
 *   sm → h-5 (20)  compact
 *   md → h-6 (24)  Figma default — tables / meta-CV (pairs with Button xs)
 *   lg → h-8 (32)  pairs with Button sm
 *   xl → h-10 (40) pairs with Button md
 *
 * Prefer StatusPill + statusPill.utils mappers for workflow status; use Tag for scores/labels.
 */

import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/utils/cn';

export type StatusVariant =
  | 'neutral'
  | 'info'
  | 'processing'
  | 'success'
  | 'warning'
  | 'error'
  | 'accent';

export type StatusSize = 'sm' | 'md' | 'lg' | 'xl';

export interface StatusPillProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  variant?: StatusVariant;
  size?: StatusSize;
  /** Show the leading dot. Default false. */
  dot?: boolean;
  /** Optional leading icon (replaces the dot if provided) */
  icon?: ReactNode;
  children: ReactNode;
}

const VARIANT_CLASSES: Record<StatusVariant, { bg: string; text: string; dot: string; pulse?: boolean }> = {
  neutral:    { bg: 'bg-dark-invirtus-soft',           text: 'text-dark-invirtus/80', dot: 'bg-dark-invirtus/40' },
  info:       { bg: 'bg-blue',                         text: 'text-dark-invirtus',    dot: 'bg-blue' },
  processing: { bg: 'bg-blue-soft',                    text: 'text-dark-invirtus',    dot: 'bg-blue', pulse: true },
  success:    { bg: 'bg-green-soft',                   text: 'text-dark-invirtus',    dot: 'bg-green' },
  warning:    { bg: 'bg-yellow-soft',                  text: 'text-dark-invirtus',    dot: 'bg-yellow' },
  error:      { bg: 'bg-red-soft',                     text: 'text-dark-invirtus',    dot: 'bg-red' },
  accent:     { bg: 'bg-purple-soft',                  text: 'text-dark-invirtus',    dot: 'bg-purple' },
};

/** Figma default (md): 24×hug, pad 11×5, gap 6, type 10/13.8. */
const SIZE_CLASSES: Record<StatusSize, { container: string; dot: string; text: string; icon: string }> = {
  sm: {
    container: 'h-5 min-h-5 px-2 gap-1 text-[9px]',
    dot: 'h-1 w-1',
    text: 'leading-none tracking-normal',
    icon: '[&_svg]:h-2.5 [&_svg]:w-2.5',
  },
  md: {
    container: 'h-6 min-h-6 px-[11px] gap-1.5 text-[10px]',
    dot: 'h-1.5 w-1.5',
    text: 'leading-[13.8px] tracking-normal',
    icon: '[&_svg]:h-2.5 [&_svg]:w-2.5',
  },
  lg: {
    container: 'h-8 min-h-8 px-3.5 gap-2 text-sm',
    dot: 'h-2 w-2',
    text: 'leading-none tracking-normal',
    icon: '[&_svg]:h-3 [&_svg]:w-3',
  },
  xl: {
    container: 'h-10 min-h-10 px-4 gap-2.5 text-base',
    dot: 'h-2.5 w-2.5',
    text: 'leading-none tracking-normal',
    icon: '[&_svg]:h-4 [&_svg]:w-4',
  },
};

export const StatusPill = ({
  variant = 'neutral',
  size = 'md',
  dot = false,
  icon,
  className,
  children,
  ...props
}: StatusPillProps) => {
  const v = VARIANT_CLASSES[variant];
  const s = SIZE_CLASSES[size];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-semibold font-body shrink-0',
        v.bg,
        v.text,
        s.container,
        props.onClick && 'cursor-pointer',
        className,
      )}
      {...props}
    >
      {icon ? (
        <span className={cn('shrink-0 flex items-center', s.icon)} aria-hidden="true">
          {icon}
        </span>
      ) : dot ? (
        <span
          aria-hidden="true"
          className={cn(
            'shrink-0 rounded-full',
            s.dot,
            v.dot,
            v.pulse && 'animate-[pulse_1.6s_ease-in-out_infinite]',
          )}
        />
      ) : null}
      <span className={s.text}>{children}</span>
    </span>
  );
};
